import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const REQUESTS_SEARCH_CACHE: { status?: string, search?: string, statusCategory?: string; } = {};

interface IRequestsSearchParams {
  status?: string[],
  search?: string,
  statusCategory?: string;
}
interface IContext {
  handleStatusChange: (status?: string[], statusCategory?: string) => void;
  handleSearchChange: (search?: string) => void;
}

export const useRequestsSearchParams = (): [IRequestsSearchParams, IContext] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [internalSearch, setInternalSearch] = useState<IRequestsSearchParams | undefined>();
  const search = searchParams.get('search') || undefined;
  const statusCategory = searchParams.get('statusCategory') || undefined;
  const statusParam = searchParams.get('status');
  const status = useMemo(() => {
    return statusParam?.split(',');
  }, [statusParam]);

  const handleStatusChange = useCallback((status?: string[], statusCategory?: string) => {
    setInternalSearch({
      search,
      status: statusCategory || !status?.length ? undefined : status,
      statusCategory: statusCategory,
    });
  }, [search]);

  const handleSearchChange = useCallback((search?: string) => {
    setInternalSearch({
      search,
      status: statusCategory || !status?.length ? undefined : status,
      statusCategory: statusCategory,
    });
  }, [status, statusCategory]);

  const urlStatus = statusCategory ? undefined : status?.join(',');
  //store values in cache in order to be used by home button link
  REQUESTS_SEARCH_CACHE.search = search;
  REQUESTS_SEARCH_CACHE.statusCategory = statusCategory;
  REQUESTS_SEARCH_CACHE.status = urlStatus;

  useEffect(() => {
    if (internalSearch) {
      setInternalSearch(undefined);
      const searchChanged = search !== internalSearch.search;
      const statusCategoryChanged = statusCategory !== internalSearch.statusCategory;
      const updateStatus = internalSearch.status?.join(',');
      const statusChanged = urlStatus !== updateStatus;
      if (searchChanged || statusCategoryChanged || statusChanged) {
        setSearchParams({
          search: internalSearch.search || [],
          status: updateStatus || [],
          statusCategory: internalSearch.statusCategory || [],
        });
      }
    }
  }, [internalSearch, search, setSearchParams, statusCategory, urlStatus]);

  return [{ search, status, statusCategory }, { handleSearchChange, handleStatusChange }];
}
